import React, { FC, useEffect, useState } from 'react';
import { LandscapeIndicatorSupportingDocumentsTemplate } from '../../templates';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import {
  deleteLandscapeDocument,
  fetchLandscapeDocumentById,
  fetchLandscapeDocumentsByLandscapeIndicatorId,
  saveLandscapeDocument,
  updateLandscapeDocument,
} from '../../../store/landscapeAPI/landscape/reducers/landscape-document.slice';
import Spinner from '../../atoms/Spinner/Spinner';
import { FileMeta } from '../../../store/mediaAPI/document/dto/request/document-save-request';
import { DocumentSaveResponseDto } from '../../../store/mediaAPI/document/dto/response/document-save-response';
import { saveDocument } from '../../../store/mediaAPI/document/reducers/document.slice';
import { LandscapeDocumentSaveRequestDto } from '../../../store/landscapeAPI/landscape/dto/request/landscape-document-save-request.dto';
import { LandscapeDocumentEntityType } from '../../../store/landscapeAPI/landscape/landcscape-document-entity-type';
import { getUUID } from '../../../utils/UUID';
import { LandscapeDocumentResponseDto } from '../../../store/landscapeAPI/landscape/dto/response/landscape-document-response.dto';
import { EditLandscapeIndicatorDocumentModal } from '../../organisms';
import { LandscapeDocumentUpdateRequestDto } from '../../../store/landscapeAPI/landscape/dto/request/landscape-document-update-request.dto';
import cn from 'classnames';
import styles from './LandscapeIndicatorSupportingDocumentsPage.module.css';
import {
  getLanguageKey,
  LocalesLanguagesConstants,
} from '../../../locales/locales';
import {
  translateObject,
  TranslateParams,
} from '../../../store/translateAPI/translate/reducers/translate.slice';

interface LandscapeIndicatorSupportingDocumentsPageProps {}

const LandscapeIndicatorSupportingDocumentsPage: FC<
  LandscapeIndicatorSupportingDocumentsPageProps
> = () => {
  const { id, landscapeIndicatorId } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  const [file, setFile] = useState<string>();
  const [fileMeta, setFileMeta] = useState<FileMeta>();
  const [fileName, setFileName] = useState<string>();
  const [fileDescription, setFileDescription] = useState<string>();
  const [fileDate, setFileDate] = useState<string>();
  const [documentToEdit, setDocumentToEdit] =
    useState<LandscapeDocumentResponseDto>();
  const [selectedDocument, setSelectedDocument] = useState<string>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [translate, setTranslate] = useState<boolean>(false);
  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );
  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );
  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const translateF = async (toLanguage: LocalesLanguagesConstants) => {
    const params: TranslateParams<LandscapeDocumentResponseDto> = {
      obj: documentToEdit!,
      from: getLanguageKey(fromLanguage)!,
      to: [getLanguageKey(toLanguage)!],
    };
    if (!documentToEdit) return;
    await dispatch(translateObject(params))
      .unwrap()
      .then(async (response: any) => {
        const updateRequest: LandscapeDocumentUpdateRequestDto = {
          compactId: documentToEdit.compactId!,
          file: documentToEdit.file ?? '',
          name: response.name ?? '',
          description: response.description,
          documentDate: documentToEdit.documentDate,
          entityId: documentToEdit.entityId,
          entityType: documentToEdit.entityType,
          id: documentToEdit.id,
          published: documentToEdit.published,
        };
        await dispatch(
          updateLandscapeDocument({
            document: updateRequest,
            language: toLanguage,
          })
        ).unwrap();
        setLanguage(toLanguage);
        setTranslate(!translate);
      });
  };

  const generateAllTranslations = async () => {
    return await Promise.allSettled(
      Object.values(LocalesLanguagesConstants).map(translateF)
    );
  };

  const generateTranslation = async () => {
    return await translateF(toLanguage);
  };

  const { landscapeDocuments, status } = useSelector(
    (state: RootState) => state.landscapeDocumentSlice
  );

  const { status: translateStatus } = useSelector(
    (state: RootState) => state.translateSlice
  );

  useEffect(() => {
    dispatch(
      fetchLandscapeDocumentsByLandscapeIndicatorId(landscapeIndicatorId!)
    );
  }, [landscapeIndicatorId, dispatch]);

  useEffect(() => {
    if (!selectedDocument) return;
    dispatch(
      fetchLandscapeDocumentById({
        id: selectedDocument,
        language: selectedLanguage,
      })
    )
      .unwrap()
      .then((resp) => setDocumentToEdit(resp));
  }, [selectedDocument, dispatch, selectedLanguage, translate]);

  const handleEditAction = async (id: string) => {
    setSelectedDocument(id);
    setShowModal(true);
  };

  const handleOnChange = async (document: LandscapeDocumentResponseDto) => {
    setDocumentToEdit(document);
  };

  const handleEdit = async (document: LandscapeDocumentResponseDto) => {
    const documentToUpdate: LandscapeDocumentUpdateRequestDto = {
      compactId: document.compactId,
      file: document.file,
      name: document.name,
      description: document.description,
      documentDate: document.documentDate,
      entityId: document.entityId,
      entityType: document.entityType,
      id: document.id,
      published: document.published,
    };
    await dispatch(
      updateLandscapeDocument({
        document: documentToUpdate,
        language: selectedLanguage,
      })
    );
    dispatch(
      fetchLandscapeDocumentsByLandscapeIndicatorId(landscapeIndicatorId!)
    );
    setShowModal(false);
  };

  const handleDownload = async (documentId: string) => {
    const documentToDownload = (
      await dispatch(
        fetchLandscapeDocumentById({
          id: documentId,
          language: LocalesLanguagesConstants.en,
        })
      )
    ).payload as LandscapeDocumentResponseDto;

    const link = document.createElement('a');
    const fileUrl = documentToDownload.file;
    link.href = documentToDownload.file;
    link.download = documentToDownload.name;
    link.target = '_blank';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(fileUrl);
  };

  const handleDelete = async (documentId: string) => {
    await dispatch(
      deleteLandscapeDocument({
        existingId: documentId,
        compactId: id!,
      })
    );
    dispatch(
      fetchLandscapeDocumentsByLandscapeIndicatorId(landscapeIndicatorId!)
    );
  };

  const handleFileOnChange = (file: string) => {
    setFile(file);
  };

  const handleFileMetaOnChange = (fileMeta: FileMeta) => {
    setFileMeta(fileMeta);
  };

  const handleFileNameOnChange = (fileName: string) => {
    setFileName(fileName);
  };

  const handleFileDescriptionOnChange = (fileDescription: string) => {
    setFileDescription(fileDescription);
  };

  const handleFileDateOnChange = (fileDate: string) => {
    setFileDate(fileDate);
  };

  const callbackFn = async () => {
    let fileSaveResponse: DocumentSaveResponseDto = {
      href: '',
    };
    if (file && file !== '' && fileMeta) {
      const formData = new FormData();
      formData.append(
        'file',
        new Blob([file], {
          type: fileMeta?.fileType,
        }),
        fileMeta?.fileSrc
      );
      fileSaveResponse = (await dispatch(saveDocument(formData)))
        .payload as DocumentSaveResponseDto;
    }
    const landscapeDocumentToSave: LandscapeDocumentSaveRequestDto = {
      compactId: id!,
      file: fileSaveResponse.href,
      name: fileName ?? '',
      description: fileDescription ?? '',
      documentDate: fileDate ?? '',
      entityId: landscapeIndicatorId!,
      entityType: LandscapeDocumentEntityType.CompactIndicator,
      id: getUUID(),
      published: true,
    };
    await dispatch(saveLandscapeDocument(landscapeDocumentToSave));
    dispatch(
      fetchLandscapeDocumentsByLandscapeIndicatorId(landscapeIndicatorId!)
    );
  };

  if (status === 'loading' || translateStatus === 'loading') {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.wrapper)}>
      {showModal && documentToEdit && (
        <EditLandscapeIndicatorDocumentModal
          show={showModal}
          setShow={setShowModal}
          handleEdit={handleEdit}
          handleOnChange={handleOnChange}
          document={documentToEdit}
          setLanguage={setLanguage}
          selectedLanguage={selectedLanguage}
          fromLanguage={fromLanguage}
          setFromLanguage={setFromLanguage}
          toLanguage={toLanguage}
          setToLanguage={setToLanguage}
          generateTranslation={generateTranslation}
          generateAllTranslations={generateAllTranslations}
        />
      )}
      <LandscapeIndicatorSupportingDocumentsTemplate
        data={landscapeDocuments ?? []}
        handleDownload={handleDownload}
        handleEditAction={handleEditAction}
        handleDelete={handleDelete}
        handleFileOnChange={handleFileOnChange}
        handleFileMetaOnChange={handleFileMetaOnChange}
        handleFileNameOnChange={handleFileNameOnChange}
        handleFileDescriptionOnChange={handleFileDescriptionOnChange}
        handleFileDateOnChange={handleFileDateOnChange}
        callbackFn={callbackFn}
      />
    </div>
  );
};

export default LandscapeIndicatorSupportingDocumentsPage;
